<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import VuePdfEmbed from 'vue-pdf-embed'
import { Loader2, Printer, Download, X } from 'lucide-vue-next'
import AsyncImage from "./AsyncImage.vue";
import mammoth from 'mammoth'
import * as XLSX from 'xlsx'
import IconExcel from './UploadIcons/IconExcel.vue';
import IconWord from './UploadIcons/IconWord.vue';
import IconPdf from './UploadIcons/IconPdf.vue';

const props = defineProps<{
  file: string | null | undefined
  show?: boolean
  grid?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const loading = ref(false)
const error = ref<string | null>(null)
const wordContent = ref('')
const excelContent = ref<{ headers: string[]; rows: any[][] } | null>(null)

const fileTypes: { [key: string]: string } = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/pdf': 'PDF',
  'image/jpeg': 'Image',
  'image/jpg': 'Image',
  'image/png': 'Image',
  'image/gif': 'Image',
  'jpeg': 'Image',
  'jpg': 'Image',
  'png': 'Image',
  'gif': 'Image',
};


const fileType = computed(() => {
  const extension = props.file ? props.file.split('.').pop()?.toLowerCase() : ''
  console.log(extension)
  return extension ? fileTypes[extension] || 'unknown' : 'unknown';
})

const handleLoadSuccess = () => {
  console.log('sucess')
  loading.value = false
  error.value = null
}

const handleLoadError = () => {
  console.log('error')
  loading.value = false
  error.value = `Failed to load ${fileType.value.toUpperCase()} file`
}

const loadWordDocument = async () => {
  try {
    if (!props.file) {
      throw new Error('File is not provided');
    }
    loading.value = true
    const response = await fetch(props.file)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    const result = await mammoth.convertToHtml({ arrayBuffer })
    wordContent.value = result.value
    loading.value = false
  } catch (error) {
    console.error('Error loading Word document:', error)
    handleLoadError()
  }
}

const loadExcelFile = async () => {
  try {
    if (!props.file) {
      throw new Error('File is not provided');
    }
    loading.value = true
    const response = await fetch(props.file)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    const workbook = XLSX.read(arrayBuffer)
    const firstSheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[firstSheetName]
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
    
    if (jsonData.length > 0) {
      const headers = jsonData[0] as string[]
      const rows = jsonData.slice(1) as any[][]
      excelContent.value = { headers, rows }
    } else {
      excelContent.value = { headers: [], rows: [] }
    }
    
    loading.value = false
  } catch (error) {
    console.error('Error loading Excel file:', error)
    handleLoadError()
  }
}

const handlePrint = async () => {
  try {
    if (!props.file) {
      throw new Error('File is not provided');
    }
    loading.value = true
    const response = await fetch(props.file)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    
    const printWindow = window.open(blobUrl, '_blank')
    
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print()
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl)
        }, 1000)
      }
    }
  } catch (error) {
    console.error('Print failed:', error)
  }
}

const handleDownload = async () => {
  try {
    if (!props.file) {
      throw new Error('File is not provided');
    }
    loading.value = true
    const response = await fetch(props.file)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `document.${fileType.value}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
  }
}

watch(() => props.show, (newValue) => {
  if (newValue) {
    loading.value = true
    error.value = null
    excelContent.value = null
    wordContent.value = ''
    console.log(newValue)
    if (fileType.value === 'docx') {
      loadWordDocument()
    } else if (fileType.value === 'excel') {
      loadExcelFile()
    }
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-stretch z-50">
    <div class="bg-white w-full flex flex-col">
      <div class="flex justify-between items-center p-4 border-b bg-white">
        <h2 class="text-xl font-semibold">{{ fileType.toUpperCase() }} Preview</h2>
        <div class="flex items-center gap-2">
          <button 
            @click="handlePrint"
            class="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Printer class="w-5 h-5" />
            <span>Print</span>
          </button>
          <button 
            @click="handleDownload"
            class="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Download class="w-5 h-5" />
            <span>Download</span>
          </button>
          <button 
            @click="emit('close')" 
            class="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X class="w-5 h-5" />
            <span>Close</span>
          </button>
        </div>
      </div>
      
      <div class="flex-1 overflow-auto bg-gray-100 text-black">
        <div v-if="loading" class="flex items-center justify-center p-8">
          <Loader2 class="w-8 h-8 animate-spin" />
          <span class="ml-2">Loading {{ fileType.toUpperCase() }}...</span>
        </div>
        
        <div v-else-if="error" class="p-4 text-red-500">
          {{ error }}
        </div>
        
        <template>
          <div v-if="fileType === 'PDF'" class="pdf-container">
            <VuePdfEmbed
              :source="file || ''"
              :width="1000"
              @rendered="handleLoadSuccess"
              @error="handleLoadError"
            />
          </div>
          
          <div v-else-if="fileType === 'Eord'" class="word-container">
            <div class="word-content" v-html="wordContent"></div>
          </div>

          <div v-else-if="fileType === 'Excel'" class="excel-container">
            <div class="excel-content">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th 
                      v-for="(header, index) in excelContent?.headers" 
                      :key="index"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(row, rowIndex) in excelContent?.rows" :key="rowIndex">
                    <td 
                      v-for="(cell, cellIndex) in row" 
                      :key="cellIndex"
                      class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <AsyncImage v-else :src="file" />
        </template>
      </div>
    </div>
  </div>
  <div v-else>
    <AsyncImage v-if="fileType == 'Image'" :src="file" />
    <IconExcel v-if="fileType == 'Excel'" />
    <IconWord v-if="fileType == 'Word'" />
    <IconPdf v-if="fileType == 'PDF'" />
  </div>
</template>

<style scoped>
.pdf-container {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: rgb(229, 231, 235);
}

.word-container {
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
  background: white;
  min-height: calc(100vh - 73px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.excel-container {
  padding: 20px;
  background: white;
  min-height: calc(100vh - 73px);
}

.excel-content {
  max-width: 100%;
  overflow-x: auto;
}

.word-content {
  font-family: 'Calibri', sans-serif;
  line-height: 1.6;
}

.word-content :deep(p) {
  margin-bottom: 1em;
}

.pdf-container :deep(iframe) {
  width: 100% !important;
  height: 100% !important;
  min-height: calc(100vh - 80px);
}

.vue-pdf-embed {
  margin: 0 auto;
  max-width: 100%;
}

:deep(.vue-pdf-embed > div) {
  margin-bottom: 20px;
  background: white;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

:deep(.vue-pdf-embed > div:last-child) {
  margin-bottom: 0;
}

:deep(.vue-pdf-embed canvas) {
  max-width: 100%;
  height: auto !important;
}
</style>